import { getAllPatients, getPatientById, getPatientDisplayName } from '../constants/mockPatients';
import { applyPatientToGenerationResult } from './patientContext';
import { buildGenerationResult } from './generationPipeline';
import { resolvePatientMatch } from './patientMatch';
import {
  getHandoverPatients,
  toSinglePatientHandoverNote,
} from './handoverUtils';

function summarizePatientLabel(patient, language = 'en') {
  if (!patient) return null;
  return {
    id: patient.id,
    name: getPatientDisplayName(patient, language),
    room: patient.room || null,
  };
}

function summarizeResultPatient(result, admittedPatients, language = 'en') {
  if (!result?.patientId) return null;
  const patient = getPatientById(result.patientId, admittedPatients);
  return summarizePatientLabel(patient, language);
}

export function buildMultiPatientContext({
  segmentIndex,
  segmentTotal,
  segmentTranscript,
  resolvedPatients = [],
  mode = 'segment',
  extra = {},
}) {
  return {
    mode,
    segmentIndex,
    segmentTotal,
    segmentTranscript: String(segmentTranscript || '').trim(),
    resolvedPatients,
    ...extra,
  };
}

export function buildSingleHandoverGenerationResult(parentResult, patientEntry, patient = null) {
  const parentHandover = parentResult.generatedNotes?.find(
    (entry) => entry.type === 'handover' && entry.note
  )?.note;
  if (!parentHandover) return parentResult;

  const singleNote = toSinglePatientHandoverNote(parentHandover, patientEntry);
  const suffix = patientEntry.patient_name || patientEntry.room || 'patient';
  const slug = String(suffix).toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 24);
  const groupId = `${parentResult.encounterGroupId}-${slug}`;

  const base = {
    ...parentResult,
    noteId: groupId,
    encounterGroupId: groupId,
    noteType: 'handover',
    generatedNotes: [{ type: 'handover', note: singleNote, error: null }],
    openaiNote: singleNote,
    companionVitalsNote: null,
    cortiNote: null,
    cortiFailed: true,
    patientId: null,
    needsPatientSelection: false,
  };

  return patient ? applyPatientToGenerationResult(base, patient) : base;
}

function handoverPatientSummary(entry) {
  const name = entry?.patient_name || 'Unknown patient';
  const room = entry?.room ? `Room ${entry.room}` : null;
  return [name, room].filter(Boolean).join(' · ');
}

export function resolveHandoverPatients(parentResult, admittedPatients = []) {
  const handoverNote = parentResult.generatedNotes?.find(
    (entry) => entry.type === 'handover' && entry.note
  )?.note;
  if (!handoverNote) return [];

  const transcript = parentResult.transcriptCleaned || parentResult.transcriptRaw || '';
  const patients = getHandoverPatients(handoverNote);

  const roster = getAllPatients(admittedPatients);

  return patients.map((entry, index) => {
    const match = resolvePatientMatch({
      patients: roster,
      transcript,
      patientHints: { name: entry.patient_name, room: entry.room },
    });
    return {
      index,
      entry,
      match,
      resolved: match.status === 'resolved' && Boolean(match.patient),
      patient: match.patient || null,
    };
  });
}

export function processMultiPatientHandover(parentResult, admittedPatients = [], language = 'en') {
  const resolutions = resolveHandoverPatients(parentResult, admittedPatients);
  if (resolutions.length < 2) {
    return { status: 'single', result: parentResult, queue: [] };
  }

  const resolved = resolutions.filter((item) => item.resolved);
  const unresolved = resolutions.filter((item) => !item.resolved);

  if (!unresolved.length) {
    const queue = resolved.map((item) =>
      buildSingleHandoverGenerationResult(parentResult, item.entry, item.patient)
    );
    return { status: 'complete', queue, result: parentResult };
  }

  const queue = resolved.map((item) =>
    buildSingleHandoverGenerationResult(parentResult, item.entry, item.patient)
  );
  const firstUnresolved = unresolved[0];

  return buildHandoverSelectionState({
    parentResult,
    resolutions,
    unresolvedEntry: firstUnresolved,
    queue,
    resolvedIndexes: resolved.map((item) => item.index),
    language,
  });
}

function buildHandoverSelectionState({
  parentResult,
  resolutions,
  unresolvedEntry,
  queue,
  resolvedIndexes,
  language,
}) {
  const selectionResult = buildSingleHandoverGenerationResult(
    parentResult,
    unresolvedEntry.entry,
    null
  );

  return {
    status: 'needs_selection',
    result: {
      ...selectionResult,
      needsPatientSelection: true,
      patientMatch: unresolvedEntry.match,
      patientHints: {
        name: unresolvedEntry.entry.patient_name,
        room: unresolvedEntry.entry.room,
      },
    },
    queue,
    multiPatientContext: buildMultiPatientContext({
      segmentIndex: unresolvedEntry.index,
      segmentTotal: resolutions.length,
      segmentTranscript: handoverPatientSummary(unresolvedEntry.entry),
      resolvedPatients: resolutions
        .filter((item) => resolvedIndexes.includes(item.index) && item.patient)
        .map((item) => summarizePatientLabel(item.patient, language)),
      mode: 'handover',
      extra: {
        parentResult,
        handoverResolutions: resolutions,
        resolvedIndexes,
      },
    }),
  };
}

export function continueHandoverPatientSelection({
  parentResult,
  handoverResolutions,
  resolvedIndexes = [],
  selectedPatient,
  currentQueue = [],
  resolvingIndex,
  language = 'en',
}) {
  const queue = [...currentQueue];
  const current = handoverResolutions.find((item) => item.index === resolvingIndex);
  if (current) {
    queue.push(
      buildSingleHandoverGenerationResult(parentResult, current.entry, selectedPatient)
    );
  }

  const nextResolvedIndexes = [...resolvedIndexes, resolvingIndex];
  const nextUnresolved = handoverResolutions.find(
    (item) => !nextResolvedIndexes.includes(item.index)
  );

  if (nextUnresolved) {
    return buildHandoverSelectionState({
      parentResult,
      resolutions: handoverResolutions,
      unresolvedEntry: nextUnresolved,
      queue,
      resolvedIndexes: nextResolvedIndexes,
      language,
    });
  }

  return { status: 'complete', queue };
}

export async function processMultiPatientSegments({
  segments,
  startIndex = 0,
  initialQueue = [],
  admittedPatients = [],
  encounterIso,
  encounterGroupId,
  cleanupFailed = false,
  sourceTranscriptRaw = null,
  sourceTranscriptCleaned = null,
  onProgress,
}) {
  const queue = [...initialQueue];
  const fullRaw = String(sourceTranscriptRaw || '').trim();
  const fullCleaned = String(sourceTranscriptCleaned || sourceTranscriptRaw || '').trim();

  for (let index = startIndex; index < segments.length; index += 1) {
    const segment = segments[index];
    onProgress?.(index, segments.length);

    let result = await buildGenerationResult({
      rawTranscript: segment.transcript,
      cleanedTranscript: segment.transcript,
      cleanupFailed,
      admittedPatients,
      patientId: segment.patientId,
      // Per-segment detection — never inherit a whole-recording forced handover type
      forcedNoteTypes: null,
      encounterIso,
      encounterGroupId: `${encounterGroupId}-p${index}`,
    });

    if (!result || result.error === 'no_clinical_content') continue;

    // Keep the full recording on each queue item for the shared transcript viewer.
    if (fullRaw || fullCleaned) {
      result = {
        ...result,
        transcriptRaw: fullRaw || result.transcriptRaw,
        transcriptCleaned: fullCleaned || result.transcriptCleaned,
        segmentTranscript: segment.transcript,
      };
    }

    if (result.needsPatientSelection) {
      return {
        status: 'needs_selection',
        result,
        queue,
        segmentIndex: index,
        segments,
        multiPatientContext: buildMultiPatientContext({
          segmentIndex: index,
          segmentTotal: segments.length,
          segmentTranscript: segment.transcript,
          resolvedPatients: queue
            .map((entry) => summarizeResultPatient(entry, admittedPatients))
            .filter(Boolean),
          mode: 'segment',
          extra: {
            segments,
            startIndex: index,
            encounterIso,
            encounterGroupId,
            cleanupFailed,
            sourceTranscriptRaw: fullRaw || null,
            sourceTranscriptCleaned: fullCleaned || null,
          },
        }),
      };
    }

    if (segment.patient && !result.patientId) {
      result = applyPatientToGenerationResult(result, segment.patient);
    }

    queue.push(result);
  }

  return { status: 'complete', queue, segments };
}
